"use client";

import { Button } from "@/components/ui/button";
import type { SelectOption } from "@/form-fields/types";
import { cn } from "@/lib/utils";

export type OptionGroupSize = "default" | "sm";

interface OptionGroupBaseProps {
  options: SelectOption[];
  onOptionHover?: (value: string | undefined) => void;
  /** `sm` for dense surfaces such as dialogs and side panels. */
  size?: OptionGroupSize;
  disabled?: boolean;
  id?: string;
  className?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

interface SingleOptionGroupProps extends OptionGroupBaseProps {
  multiple?: false;
  allowDeselect?: boolean;
  value: string;
  onChange: (value: string) => void;
}

interface MultiOptionGroupProps extends OptionGroupBaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

export type OptionGroupProps = SingleOptionGroupProps | MultiOptionGroupProps;

/**
 * Above this label length an option set reads as descriptive sentences rather
 * than short tags, and a stacked full-width chip is more legible than a chip in
 * a wrapping row. (e.g. "Vestibular / Balance / Dizziness" → wrapping chips;
 * "Sudden — after a specific event (head impact, fall, accident)" → stacked.)
 */
const DESCRIPTIVE_LABEL_LENGTH = 32;

/**
 * Field where every option is visible (one click to pick, no dropdown).
 * Single-select by default; pass `multiple` for an array-valued toggle group
 * ("select all that apply"). Content-adaptive layout, one chip style
 * throughout: short tag-like options wrap as a horizontal chip row; long
 * descriptive options stack as full-width chips. Selected state is a subtle
 * tint + accent, never a heavy solid fill.
 */
export function OptionGroup(props: OptionGroupProps) {
  const { options, disabled, id, className, size = "default" } = props;
  const small = size === "sm";
  const multiple = props.multiple === true;
  const toggles = multiple || (!props.multiple && props.allowDeselect === true);
  const stacked = options.some((opt) => typeof opt.label === "string" && opt.label.length > DESCRIPTIVE_LABEL_LENGTH);

  const isSelected = (optValue: string) =>
    multiple ? (props.value ?? []).includes(optValue) : props.value === optValue;

  const handleSelect = (optValue: string) => {
    if (props.multiple) {
      const current = props.value ?? [];
      props.onChange(current.includes(optValue) ? current.filter((v) => v !== optValue) : [...current, optValue]);
    } else {
      props.onChange(props.allowDeselect && props.value === optValue ? "" : optValue);
    }
  };

  return (
    <div
      id={id}
      onPointerLeave={() => props.onOptionHover?.(undefined)}
      role={toggles ? "group" : "radiogroup"}
      aria-invalid={props["aria-invalid"]}
      aria-describedby={props["aria-describedby"]}
      className={cn("flex", small ? "gap-1.5" : "gap-2", stacked ? "flex-col items-stretch" : "flex-wrap", className)}
    >
      {options.map((opt) => {
        const selected = isSelected(opt.value);
        return (
          <Button
            key={opt.value}
            type="button"
            size={small ? "xs" : "sm"}
            variant="outline"
            role={toggles ? undefined : "radio"}
            aria-pressed={toggles ? selected : undefined}
            aria-checked={toggles ? undefined : selected}
            disabled={disabled}
            onClick={() => handleSelect(opt.value)}
            onPointerEnter={() => props.onOptionHover?.(opt.value)}
            className={cn(
              "font-normal",
              // Stacked chips fill the row and wrap their text.
              stacked
                ? cn("h-auto w-full justify-start rounded-md text-left whitespace-normal", small ? "py-1" : "py-1.5")
                : "rounded-full",
              // Selected carries weight as well as colour: the focus ring alone
              // reads as strongly as a tint, so tint-only selection was
              // indistinguishable from "the last thing I clicked".
              selected
                ? "border-primary bg-primary/10 text-primary font-medium ring-1 ring-primary/40 hover:bg-primary/15 hover:text-primary dark:bg-primary/15"
                : "text-muted-foreground",
            )}
          >
            <span className={cn(stacked && "flex-1")}>{opt.label}</span>
          </Button>
        );
      })}
    </div>
  );
}
